"use client";
import { ProductImage } from "@/__generated__/sdk";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Card, CardActionArea, Fade, Grid, Icon, IconButton, Stack, Typography } from "@mui/material";
import Image from "next/image";
import Fancybox, { FancyboxRef } from "./Fancybox";
import useEmblaCarousel from "embla-carousel-react";
import { usePrevNextButtons } from "@/lib/usePrevNextButtons";
import { EmblaCarouselType } from "embla-carousel";
import Arrow2LeftIcon from "@/app/_components/Icons/Arrow2LeftIcon";
import Arrow2RightIcon from "@/app/_components/Icons/Arrow2RightIcon";
import { shimmer, toBase64 } from "@/app/(main)/_components/SITESEARCH/ProductImageComponent";

type Props = {
  images: ProductImage[];
};
export default function ProductImagesView({ images }: Props) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    loop: false,
    direction: "rtl",
    dragFree: false,
    align: "start",
  });
  // const emblaMainConRef = useRef<Element | null>(null);
  // const isHoveringEmblaMainRef = useHoverDirty(emblaMainConRef);
  const {
    nextBtnDisabled: emblaMainNextBtnDisabled,
    onNextButtonClick: emblaMainOnNextButtonClick,
    onPrevButtonClick: emblaMainOnPrevButtonClick,
    prevBtnDisabled: emblaMainPrevBtnDisabled,
  } = usePrevNextButtons(emblaMainApi as EmblaCarouselType | undefined);

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    direction: "rtl",
  });
  // const {
  //   nextBtnDisabled: emblaThumbsNextBtnDisabled,
  //   onNextButtonClick: emblaThumbsOnNextButtonClick,
  //   onPrevButtonClick: emblaThumbsOnPrevButtonClick,
  //   prevBtnDisabled: emblaThumbsPrevBtnDisabled,
  // } = usePrevNextButtons(emblaThumbsApi as EmblaCarouselType | undefined);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi],
  );
  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  const fancyboxRef = useRef<FancyboxRef>(null);
  const [fancyboxIsActive, setFancyboxIsActive] = useState(false);

  const slideSpacing = "1rem";
  const slideSize = "100%";

  const thumbsSlideSpacing = "0.8rem";
  return (
    <Stack>
      {/*<style>.h_iframe-aparat_embed_frame{position:relative;}.h_iframe-aparat_embed_frame*/}
      {/*  .ratio{display:block;width:100%;height:auto;}.h_iframe-aparat_embed_frame*/}
      {/*  iframe{position:absolute;top:0;left:0;width:100%;height:100%;}</style>*/}

      <Fancybox
        ref={fancyboxRef}
        options={{
          id: "classic-fancybox",

          Thumbs: {
            type: "classic",
          },
        }}
        setFancyboxIsActive={setFancyboxIsActive}
      >
        {/* embla */}
        <Card elevation={0} sx={{ position: "relative", width: "100%" }}>
          {/* embla__viewport */}
          <Box
            sx={{
              "& .emblaMainNextBtn, & .emblaMainPrevBtn": {
                opacity: { xs: 1, md: "0 !important" },
                display: "flex",
                transform: "opacity 225ms cubic-bezier(0.4, 0, 0.2, 1)",
              },
              "&:hover .emblaMainNextBtn, &:hover .emblaMainPrevBtn": {
                opacity: "1 !important",
              },
            }}
          >
            <Box sx={{ overflow: "hidden", borderRadius: 3, position: "relative" }} ref={emblaMainRef}>
              {/* embla__container */}
              <Box
                sx={{
                  display: "flex",
                  touchAction: "pan-y pinch-zoom",
                  marginLeft: `calc(${slideSpacing} * -1)`,
                }}
              >
                {images.map((image, index) => (
                  // embla__slide
                  <Box
                    key={index}
                    sx={{
                      transform: "translate3d(0, 0, 0)",
                      flex: `0 0 ${slideSize}`,
                      minWidth: 0,
                      paddingLeft: slideSpacing,
                    }}
                  >
                    <Box
                      className="f-carousel__slide"
                      data-fancybox="gallery"
                      data-src={image?.address}
                      sx={{
                        // aspectRatio: { xs: 0.87, md: 0.78 },
                        aspectRatio: 1,
                        overflow: "hidden",
                        borderRadius: 3,
                        userSelect: "none",
                        position: "relative",
                        width: "100%",
                      }}
                    >
                      <Image
                        alt={image?.alt || ""}
                        src={image?.address}
                        priority={true}
                        loading="eager"
                        placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(700, 475))}`}
                        unoptimized
                        draggable={false}
                        fill
                        style={{ objectFit: "cover" }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
              <Fade in={!emblaMainNextBtnDisabled}>
                <Box
                  className="emblaMainNextBtn"
                  sx={(theme) => ({
                    position: "absolute",
                    alignItems: "center",
                    right: 0,
                    top: 0,
                    height: "100%",
                    width: "45px",
                    zIndex: 1,
                    // background: `linear-gradient(90deg,  ${theme.palette.background.default},transparent)`,
                  })}
                >
                  <IconButton
                    onClick={() => {
                      emblaMainOnNextButtonClick();
                    }}
                    aria-label="Next Button"
                  >
                    <Icon
                      sx={{
                        "& svg": {
                          filter: "drop-shadow(rgba(255, 255, 255, 1) 1px 0px 0.5px)",
                        },
                      }}
                    >
                      <Arrow2LeftIcon />
                    </Icon>
                  </IconButton>
                </Box>
              </Fade>
              <Fade in={!emblaMainPrevBtnDisabled}>
                <Box
                  className="emblaMainPrevBtn"
                  sx={(theme) => ({
                    position: "absolute",
                    alignItems: "center",
                    left: 0,
                    top: 0,
                    height: "100%",
                    width: "45px",
                    zIndex: 1,
                    // background: `linear-gradient(90deg, transparent,  ${theme.palette.background.default})`,
                  })}
                >
                  <IconButton
                    aria-label="Prev Button"
                    onClick={() => {
                      emblaMainOnPrevButtonClick();
                    }}
                  >
                    <Icon
                      sx={{
                        "& svg": {
                          filter: "drop-shadow(rgba(255, 255, 255, 1) -1px 0px 0.5px)",
                        },
                      }}
                    >
                      <Arrow2RightIcon />
                    </Icon>
                  </IconButton>
                </Box>
              </Fade>
            </Box>
          </Box>

          {/* embla-thumbs */}
          <Box
            sx={{
              marginTop: thumbsSlideSpacing,
            }}
          >
            {/* embla-thumbs__viewport */}
            <Box sx={{ overflow: "hidden", borderRadius: 3, position: "relative" }} ref={emblaThumbsRef}>
              {/* embla-thumbs__container */}
              <Box sx={{ display: "flex", flexDirection: "row", marginLeft: `calc(${thumbsSlideSpacing} * -1)` }}>
                {images.map((image, index) => {
                  const selected = index === selectedIndex;
                  return (
                    <Box
                      key={index}
                      className="embla-thumbs__slide"
                      sx={{
                        flex: { xs: "0 0 20%", sm: "0 0 27%", md: "0 0 22%" },
                        minWidth: "0",
                        paddingLeft: thumbsSlideSpacing,
                      }}
                    >
                      <Box
                        onClick={() => onThumbClick(index)}
                        sx={{
                          overflow: "hidden",
                          borderRadius: 3,
                          position: "relative",
                          userSelect: "none",
                          aspectRatio: 1,
                          width: "100%",
                          transition: "0.2s all",
                          opacity: selected ? 1 : 0.7,
                          cursor: "pointer",
                        }}
                      >
                        {/* {isLastOne && (
                       <Box
                         sx={{
                           position: "absolute",
                           top: 0,
                           zIndex: 1,
                           bottom: 0,
                           left: 0,
                           right: 0,
                           display: "flex",
                           alignItems: "center",
                           justifyContent: "center",
                           backgroundColor: "rgba(255,255,255,0.6)",
                           backdropFilter: "blur(5px)",
                         }}
                       >
                         <Icon fontSize="large" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                           <MoreIcon />
                         </Icon>
                       </Box>
                     )} */}
                        <Image
                          alt={image?.alt || ""}
                          placeholder={`data:image/svg+xml;base64,${toBase64(shimmer(700, 475))}`}
                          draggable={false}
                          loading="lazy"
                          src={image?.address}
                          fill
                          style={{ objectFit: "cover" }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Card>

        {/* <Grid container spacing={1.7}>
          <Grid
            item
            xs={12}

            // data-thumb-src="https://lipsum.app/id/60/200x150"
          >
            <Box
              className="f-carousel__slide"
              data-fancybox="gallery"
              data-src={mainImage?.address}
              sx={{ aspectRatio: 0.78, overflow: "hidden", borderRadius: 3, userSelect: "none", position: "relative", width: "100%" }}
            >
              <Image alt={mainImage?.alt || ""} src={mainImage?.address} draggable={false} fill style={{ objectFit: "cover" }} />
            </Box>
          </Grid>
          {thumbImages.map((image, index) => {
            const isLastOne = thumbImages.length - 1 === index;
            return (
              <Grid
                key={index}
                item
                xs={12 / 5}
                // data-thumb-src="https://lipsum.app/id/61/200x150"
              >
                <Box
                  className="f-carousel__slide"
                  data-fancybox="gallery"
                  data-src={image?.address}
                  sx={{ overflow: "hidden", borderRadius: 3, position: "relative", userSelect: "none", aspectRatio: 1, width: "100%" }}
                >
                  {isLastOne && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        zIndex: 1,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255,255,255,0.6)",
                        backdropFilter: "blur(5px)",
                      }}
                    >
                      <Icon fontSize="large" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <MoreIcon />
                      </Icon>
                    </Box>
                  )}
                  <Image alt={image?.alt || ""} draggable={false} src={image?.address} fill style={{ objectFit: "cover" }} />
                </Box>
              </Grid>
            );
          })}
          {otherImages.map((image, index) => {
            return <a key={index} className="f-carousel__slide" data-fancybox="gallery" href={image.address} style={{ display: "none" }} />;
          })}
        </Grid> */}
      </Fancybox>
    </Stack>
  );
}
